import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ContractsRequestModel } from '../../helpers/objects/request/contracts/contracts-request/contracts-request-model';
import { ContractsResponseModel } from '../../helpers/objects/response/contracts/contracts-response/contracts-response-model';
import { GraphNewContractsResponseModel } from '../../helpers/objects/response/graph-new-contracts/graph-new-contracts-response-model';
import { GraphActiveWalletResponseModel } from '../../helpers/objects/response/graph-active-wallet/graph-active-wallet-response-model';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { ContractDetailModel } from '../../helpers/objects/dto/contract-detail/contract-detail-model';
import { ContractDocumentsRequestModel } from '../../helpers/objects/request/contracts/contract-documents-request/contract-documents-request-model';
import { DocumentsModel } from '../../helpers/objects/dto/documents/documents-model';
import { ContractDocumentContentRequestModel } from '../../helpers/objects/request/contracts/contract-document-content-request/contract-document-content-request-model';
import { ContractDocumentContentResponseModel } from '../../helpers/objects/response/contracts/contract-document-content-response/contract-document-content-response-model';
import { GraphWalletValuesResponseModel } from '../../helpers/objects/response/graph-wallet-values/graph-wallet-values-response-model';
import { ContractInsuredPeopleResponse } from '../../helpers/objects/response/contracts/contract-insured-people-response/contract-insured-people-response';
import { Router, UrlSerializer } from '@angular/router';
import { ContractBenefitsPlanRequestModel } from '../../helpers/objects/request/contracts/contract-benefits-plan/contract-benefits-plan-request-model';
import { ContractBenefitsPlansResponseModel } from '../../helpers/objects/response/contracts/contract-benefits-plans/contract-benefits-plans-response-model';
import { FileModel } from '../../helpers/objects/dto/file/file-model';
import { TranslateService } from '@ngx-translate/core';
import { CpDocumentRequestModel } from '@fh-client-interaction-frontends/fh-client-portal-service';
import {
  CpDocumentResponseModel
} from '../../../../../../../client-portal/data-access/fh-client-portal-service/src/lib/helpers/objects/response/document/cp-document-response-model';
import {
  CompanyDocumentsSetupModel
} from '../../helpers/objects/dto/company-documents-setup/company-documents-setup-model';

@Injectable({
  providedIn: 'root'
})
export class ContractsService {
  appConfig: EnvironmentModel = new EnvironmentModel();

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel,
    private serializer: UrlSerializer,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.appConfig = appConfigTemp;
  }

  get(filter: ContractsRequestModel): Observable<ContractsResponseModel> {
    if (filter.pageSize == undefined) {
      filter.pageSize = 5;
    }

    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });

    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId, {
        params: httpParams
      })
      .pipe(map((response) => response.body as ContractsResponseModel));
  }

  getNewContractsChart(): Observable<GraphNewContractsResponseModel[]> {
    return this.http
      .get<any>(this.appConfig.api + 'new-production/' + this.appConfig.apiPartnershipId + '/new-contracts')
      .pipe(map((response) => response.body.newContracts as GraphNewContractsResponseModel[]));
  }

  getWalletValuesChart(): Observable<any> {
    return this.http
      .get<any>(this.appConfig.api + 'new-production/' + this.appConfig.apiPartnershipId + '/portfolio/value')
      .pipe(map((response) => response.body.walletValues as GraphWalletValuesResponseModel[]));
  }

  getActiveWallet(): Observable<any> {
    return this.http
      .get<any>(this.appConfig.api + 'new-production/' + this.appConfig.apiPartnershipId + '/portfolio')
      .pipe(map((response) => response.body.activeWallet as GraphActiveWalletResponseModel));
  }

  getDetails(id: string): Observable<ContractDetailModel> {
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + id)
      .pipe(map((response) => response.body.contractDetail as ContractDetailModel));
  }

  getDocuments(filter: ContractDocumentsRequestModel): Observable<DocumentsModel[]> {
    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });
    return this.http
      .get<any>(this.appConfig.api + 'setup/contract-documents/' + this.appConfig.apiPartnershipId, {
        params: httpParams
      })
      .pipe(map((response) => response.body.documents as DocumentsModel[]));
  }

  getBenefitsPlan(filter: ContractBenefitsPlanRequestModel): Observable<ContractBenefitsPlansResponseModel> {
    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });

    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(filter.PolicyId) + '/benefitPlan', {
        params: httpParams
      })
      .pipe(map((response) => response.body as ContractBenefitsPlansResponseModel));
  }

  getDocumentContent(filter: ContractDocumentContentRequestModel): Observable<ContractDocumentContentResponseModel> {
    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });
    return this.http
      .get<any>(this.appConfig.api + 'setup/contract-documents/' + this.appConfig.apiPartnershipId + '/content', {
        params: httpParams
      })
      .pipe(map((response) => response.body as ContractDocumentContentResponseModel));
  }

  getContractInsuredPeople(contractId: string): Observable<ContractInsuredPeopleResponse> {
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/insuredPeople')
      .pipe(map((response) => response.body as ContractInsuredPeopleResponse));
  }

  getCompanyContract(contractId: string): Observable<any> {
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/company-detail')
      .pipe(map((response) => response.body.company));
  }

  getCompanyLastMonthlyFilesContract(contractId: string): Observable<FileModel[]> {
    return this.http
      .get<any>(
        this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/company-detail/last-files-information'
      )
      .pipe(map((response) => response.body.files as FileModel[]));
  }

  getCompanyDocumentSetupContract(contractId: string): Observable<CompanyDocumentsSetupModel[]> {
    return this.http
      .get<any>(
        this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/documents-setup?featureName=POLICY_DOCUMENTS')
      .pipe(map((response) => response.body.documentTypeConfig as CompanyDocumentsSetupModel[]));
  }


  getCompanyDocuments(contractId: string, filters: CpDocumentRequestModel): Observable<CpDocumentResponseModel> {
    const params = new HttpParams({
      fromObject: filters as any
    });
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/'+ encodeURIComponent(contractId) + '/documents', { params })
      .pipe(map((response) => response.body as CpDocumentResponseModel));
  }

  editCompanyContractDetails(contractId: string, companyData: ContractDetailModel): Observable<any> {
    return this.http
      .put<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/company-detail', companyData)
      .pipe(map((response) => response as ContractDetailModel));
  }

  editCompanyContractContributory(contractId: string, contributoryData: any): Observable<string> {
    return this.http
      .put<any>(
        this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/company-detail/contributives',
        contributoryData
      )
      .pipe(map((response) => response.body.nextContributiveDateEffect as string));
  }

  download(filter: ContractsRequestModel): string {
    const extraParams = {
      pageSize: 10000,
      page: 1,
      Authorization: sessionStorage.getItem('token'),
      location: this.translateService.currentLang
    };
    const params = { ...filter, ...extraParams };
    const query = this.router.createUrlTree([], { queryParams: params });

    return this.appConfig.api + 'contracts-worksheet/' + this.appConfig.apiPartnershipId + '?' + (this.serializer.serialize(query).split('?')[1] || '');
  }

  downloadInsuredPeople(policyId: string) {
    const extraParams = {
      pageSize: 10000,
      page: 1,
      Authorization: sessionStorage.getItem('token'),
      location: this.translateService.currentLang
    };

    const params = { ...extraParams };
    const query = this.router.createUrlTree([], { queryParams: params });

    return (
      this.appConfig.api +
      policyId +
      '/insured-people-worksheet/' +
      this.appConfig.apiPartnershipId +
      '?' +
      (this.serializer.serialize(query).split('?')[1] || '')
    );
  }

  deleteInsuredPerson(token: string, cardNumber: string): Observable<any> {
    return this.http.delete<any>(
      this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(token) + '/company-detail/insured-persons/' + cardNumber
    );
  }

  sendMonthlyFiles(contractId: string , filesGuid:any){
    return this.http.post<any>(
      this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/' + encodeURIComponent(contractId) + '/company-detail/files',
      filesGuid
    )
      .pipe(map((response) => response.body));
  }

  downloadCompanyDocuments(contractId:string, filters:any): Observable<any>{
    const params = new HttpParams({
      fromObject: filters as any
    });
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + this.appConfig.apiPartnershipId + '/'+ encodeURIComponent(contractId) + '/documents/content', { params })
      .pipe(map((response) => response.body));
  }

}
